import React from 'react';
import { Helmet } from 'react-helmet';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { initializeHotjar } from '@edx/frontend-enterprise-hotjar';
import { useIntl } from '@edx/frontend-platform/i18n';
import { logError } from '@edx/frontend-platform/logging';
import { getCookies } from '@edx/frontend-platform/i18n/lib';

import { AppContext } from '@edx/frontend-platform/react';

import {
  actions,
  selectors,
} from 'data/redux';
import store from 'data/store';
import { reduxHooks } from 'hooks';

import track from 'tracking';

import fakeData from 'data/services/lms/fakeData/courses';

import {
  AppWrapper, AiChatModal, AiChatProvider, isPlatformAdmin,
} from '@blend-ed/blendxplatform';

import { getConfig } from '@edx/frontend-platform';
import OrgPageWrapper from './pages/select-org/OrgPageWrapper';
import { aiChatRegistry } from './containers/aiChatRegistry';
import './App.scss';
import messages from './messages';

export const App = () => {
  const { authenticatedUser } = React.useContext(AppContext);
  const { formatMessage } = useIntl();
  const loadData = reduxHooks.useLoadData();
  const navigate = useNavigate();
  const location = useLocation();
  const isOrgSelectPage = location.pathname.endsWith('/organizations');

  // Platform admins shouldn't land on the learner dashboard after login.
  // Only redirect from the root path so admin-followed deep links still work.
  React.useEffect(() => {
    const adminDashboardUrl = getConfig().ADMIN_DASHBOARD_URL;
    const isRootPath = location.pathname === '/' || location.pathname === '';
    if (isRootPath && adminDashboardUrl && isPlatformAdmin(authenticatedUser?.roles)) {
      window.location.replace(adminDashboardUrl);
    }
  }, [authenticatedUser, location.pathname]);

  React.useEffect(() => {
    const orgName = getCookies().get('orgName');
    if (!orgName) {
      const href = window.location.href;
      navigate(`/organizations?next=${href}`);
    }
  }, []);

  React.useEffect(() => {
    if (authenticatedUser?.administrator || getConfig().NODE_ENV === 'development') {
      window.loadEmptyData = () => {
        loadData({ ...fakeData.globalData, courses: [] });
      };
      window.loadMockData = () => {
        loadData({
          ...fakeData.globalData,
          courses: [
            ...fakeData.courseRunData,
            ...fakeData.entitlementData,
          ],
        });
      };
      window.store = store;
      window.selectors = selectors;
      window.actions = actions;
      window.track = track;
    }
    if (getConfig().HOTJAR_APP_ID) {
      try {
        initializeHotjar({
          hotjarId: getConfig().HOTJAR_APP_ID,
          hotjarVersion: getConfig().HOTJAR_VERSION,
          hotjarDebug: !!getConfig().HOTJAR_DEBUG,
        });
      } catch (error) {
        logError(error);
      }
    }
  }, [authenticatedUser, loadData]);

  return (
    <AiChatProvider>
      <Helmet>
        <title>{formatMessage(messages.pageTitle)}</title>
        <link rel="shortcut icon" href={getConfig().FAVICON_URL} type="image/x-icon" />
      </Helmet>
      {isOrgSelectPage ? (
        <OrgPageWrapper>
          <main id="main">
            <Outlet />
          </main>
        </OrgPageWrapper>
      ) : (
        <AppWrapper hasuraRole="learner" notificationsHref="/notifications">
          <main id="main">
            <Outlet />
          </main>
          <AiChatModal
            apiBasePath="/blendxai/assistant"
            sessionSource="learner"
            componentRegistry={aiChatRegistry}
          />
        </AppWrapper>
      )}
    </AiChatProvider>
  );
};

export default App;
